import React, { useCallback } from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import { PropertyMap } from 'app/shared/typings';
import { Box, Typography } from 'app/shared/components/atoms';
import { MappingsProps } from 'app/fan/components/templates/EventPage/components/EventCharacteristicsList/utils';
import { ReactComponent as Information } from 'icons/info.svg';

interface AdditionalInformationProps {
  propertyMap?: PropertyMap;
}

export function useAdditionalInformationMapping({
  formatMessage,
}: MappingsProps) {
  const { colors } = useCurrentTheme();
  const intlPrefix = 'event.eventCharacteristicsList.additionalInformation';

  return useCallback(({ propertyMap }: AdditionalInformationProps) => {
    if (!propertyMap) {
      return null;
    }

    function getSmokingAndWeedMessage() {
      if (propertyMap?.smokingPermitted && propertyMap?.fourTwentyFriendly) {
        return formatMessage({
          id: `${intlPrefix}.420FriendlySmokingPermitted`,
        });
      } else if (propertyMap?.smokingPermitted) {
        return formatMessage({ id: `${intlPrefix}.smokingPermitted` });
      } else if (propertyMap?.fourTwentyFriendly) {
        return formatMessage({ id: `${intlPrefix}.420Friendly` });
      } else if (propertyMap?.smokingNotPermitted) {
        return formatMessage({ id: `${intlPrefix}.smokingNotPermitted` });
      }

      return null;
    }

    function getParkingMessage() {
      if (propertyMap?.noParking) {
        return formatMessage({ id: `${intlPrefix}.noParking` });
      } else if (propertyMap?.freePublicParkingNearby) {
        return formatMessage({ id: `${intlPrefix}.freePublicParkingNearby` });
      } else if (propertyMap?.paidParking) {
        return formatMessage({ id: `${intlPrefix}.paidParking` });
      }
      return null;
    }

    function getAccessibleParkingMessage() {
      if (propertyMap?.noParking && propertyMap.accessibleParkingAvailable) {
        return formatMessage({
          id: `${intlPrefix}.howeverAccessibleParkingAvailable`,
        });
      } else if (propertyMap?.accessibleParkingAvailable) {
        return formatMessage({
          id: `${intlPrefix}.accessibleParkingAvailable`,
        });
      }
      return null;
    }

    const smokingAndWeedMessage = getSmokingAndWeedMessage();
    const parkingMessage = getParkingMessage();
    const accessibleParkingMessage = getAccessibleParkingMessage();

    const shouldDisplayAdditionalInformationSection =
      !!smokingAndWeedMessage || !!parkingMessage || !!accessibleParkingMessage;

    if (shouldDisplayAdditionalInformationSection) {
      return {
        id: 'additional_information',
        title: 'Additional information',
        icon: Information,
        openByDefault: true,
        details: (
          <Box flexDirection="column" gap={3}>
            {smokingAndWeedMessage && (
              <Typography color={colors.primary.white.base}>
                {smokingAndWeedMessage}
              </Typography>
            )}
            {parkingMessage && (
              <Typography color={colors.primary.white.base}>
                {parkingMessage}
              </Typography>
            )}
            {accessibleParkingMessage && (
              <Typography color={colors.primary.white.base}>
                {accessibleParkingMessage}
              </Typography>
            )}
          </Box>
        ),
      };
    }

    return null;
  }, []);
}
